import React from 'react';
import { connect } from 'react-redux';
import { debounce, groupBy } from 'lodash';
import pt from 'prop-types';
import actions from 'partners/actions';

import { classNames } from 'utils';

import Icon from 'components/icon';
import Tooltip from 'components/tooltip';
import AppIcon from 'components/apps/icon';
import {
  getAppDisplayId,
  MAX_APP_ID_DISPLAY_LEN,
  renderAppTypeLabel,
} from 'utils/app';
import { truncatedText } from 'utils/collection';
import SelectRecordsModal from './SelectRecords';

const fields = {
  name: {
    columns: [
      {
        id: 'icon',
        label: 'App',
        type: 'image',
        func(app) {
          return <AppIcon app={app} size={48} />;
        },
      },
      {
        id: 'name',
        func(app) {
          const appId = getAppDisplayId(app);

          return (
            <span className="select-records-app-name">
              <span className="select-records-app-name-title">{app.name}</span>
              {appId && (
                <span className="select-records-app-name-subtitle muted">
                  {truncatedText(appId, MAX_APP_ID_DISPLAY_LEN)}
                </span>
              )}
            </span>
          );
        },
      },
    ],
  },
  store: {
    label: 'Store',
    func(app) {
      return app.client?.name || null;
    },
  },
  type: {
    label: 'Type',
    // No tooltip: its popover needs a `client` context the partners root
    // does not provide.
    func(app) {
      return app.type ? renderAppTypeLabel(app.type, false) : undefined;
    },
  },
  status: {
    label: 'Status',
    func(app, props) {
      if (app._status === 'connected') {
        return (
          <span className="select-records-status connected">
            <Icon fa="check" />
            Connected
          </span>
        );
      }

      const label = app._status === 'unavailable' ? 'Unavailable' : 'Available';
      const tooltipMessage = props?.disabledRecords?.[app.id];

      if (typeof tooltipMessage !== 'string') {
        return label;
      }

      return (
        <span
          className={classNames('select-records-status', {
            muted: app._status === 'unavailable',
          })}
        >
          {label}
          <Tooltip message={tooltipMessage}>
            <Icon fa="info-circle" />
          </Tooltip>
        </span>
      );
    },
  },
};

const mapDispatchToProps = (dispatch) => ({
  fetchCandidates: (query) => {
    return dispatch(actions.apps.fetchLinkCandidates(query));
  },
});

/**
 * Maps a candidate's server-computed status onto the row: connected apps and
 * apps another partner or store holds are shown for context only.
 */
function getCandidateStatus(app) {
  // Already connected: no tooltip needed here.
  if (app.connected) {
    return { status: 'connected', disabled: true };
  }

  if (app.other_partner) {
    const { id, name } = app.other_partner;
    const partnerLabel = name ? `${name} (${id})` : id;

    return {
      status: 'unavailable',
      disabled: `This app is connected to the ${partnerLabel} partner dashboard. Disconnect it there to connect it here.`,
    };
  }

  if (app.conflict) {
    return {
      status: 'unavailable',
      disabled: `This app is already connected from ${
        app.conflict.client?.name || 'another store'
      }. Disconnect it there to connect this instance.`,
    };
  }

  return { status: 'available', disabled: false };
}

export class LinkApps extends React.PureComponent {
  static propTypes = {
    title: pt.string,
    action: pt.oneOfType([pt.string, pt.func]),
    width: pt.number,
    onSubmit: pt.func,
    onCancel: pt.func,

    fetchCandidates: pt.func,
  };

  static contextTypes = {
    notifyError: pt.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      collection: {},
      disabledRecords: {},
      filter: '',
      // The search of the loaded page; `filter` runs ahead of it while typing
      search: '',
    };

    // Orders responses: only the latest request is applied
    this.request = 0;
  }

  // Below this a filter is more chrome than help; the list is already scannable.
  static FILTER_THRESHOLD = 4;

  static PAGE_LIMIT = 100;

  static SEARCH_DELAY = 300;

  componentDidMount() {
    this.fetchPage(1, '');
  }

  componentWillUnmount() {
    this.searchDebounced.cancel();
    // Drops a response still in flight
    this.request += 1;
  }

  // The server computes the candidate set (stores the user owns or is
  // authorized with, plus stores connected to this partner account), and each
  // candidate's status, conflicts included from every app of the partner.
  async fetchPage(page, search) {
    const { fetchCandidates } = this.props;
    const request = ++this.request;

    this.setState({ loading: true });

    const response = await fetchCandidates({
      page,
      limit: LinkApps.PAGE_LIMIT,
      ...(search && { search }),
    });

    if (request !== this.request) {
      return;
    }

    const disabledRecords = {};

    const results = (response?.results || []).map((app) => {
      const { status, disabled } = getCandidateStatus(app);

      if (disabled) {
        disabledRecords[app.id] = disabled;
      }

      return { ...app, _status: status };
    });

    this.setState({
      collection: {
        ...response,
        results,
        count: response?.count || 0,
        page: response?.page || page,
        // What the pagination reads to page through
        totalPages:
          response?.page_count || Object.keys(response?.pages || {}).length,
      },
      disabledRecords,
      search,
      loading: false,
    });
  }

  searchDebounced = debounce(() => {
    this.fetchPage(1, this.state.filter.trim());
  }, LinkApps.SEARCH_DELAY);

  onChangeFilter = (event) => {
    this.setState({ filter: event.target.value }, this.searchDebounced);
  };

  onClickPage = (event) => {
    event.preventDefault();

    const page = Number(event.currentTarget.dataset.page);

    if (page > 0) {
      this.fetchPage(page, this.state.search);
    }
  };

  // Apps pushed to multiple stores share the same private_id. Only one
  // instance of a given app can be connected to a partner account at once:
  // a selected instance, on any page, disables the others on this one.
  getConflictedApps = (selectedRecords, records) => {
    const selectedPrivateIds = new Set(
      selectedRecords.map((app) => app.private_id).filter(Boolean),
    );

    if (selectedPrivateIds.size === 0) {
      return null;
    }

    const selectedIds = new Set(selectedRecords.map((app) => app.id));
    const { disabledRecords } = this.state;

    return records.reduce((acc, app) => {
      if (
        app.private_id &&
        // Keep the reason a row is already disabled for
        !disabledRecords[app.id] &&
        !selectedIds.has(app.id) &&
        selectedPrivateIds.has(app.private_id)
      ) {
        acc[app.id] =
          'Only one instance of an app can be connected. Deselect the other to choose this one.';
      }

      return acc;
    }, {});
  };

  // When selecting all, only one instance of each private_id should end up
  // selected: whichever one is already selected (on any page), or the first
  // instance (in list order) if none of them are.
  getSelectAllExcept = (records, selectedRecords = []) => {
    const selectedByPrivateId = new Map(
      selectedRecords
        .filter((app) => app.private_id)
        .map((app) => [app.private_id, app.id]),
    );
    const groups = groupBy(
      records.filter((app) => app.private_id),
      'private_id',
    );

    return Object.entries(groups).reduce((except, [privateId, group]) => {
      const keeperId = selectedByPrivateId.get(privateId) || group[0].id;

      group.forEach((app) => {
        if (app.id !== keeperId) {
          except[app.id] = true;
        }
      });

      return except;
    }, {});
  };

  onSubmit = (ids, selectedRecords = []) => {
    const { notifyError } = this.context;
    const { onSubmit } = this.props;

    // Apps pushed to multiple stores share the same private_id. Only one
    // instance of a given app can be connected to a partner account at once,
    // so selecting more than one instance of the same app is not allowed.
    // The selection spans pages, so its records come with it.
    const groupedByPrivateId = groupBy(
      selectedRecords.filter((app) => app.private_id),
      'private_id',
    );

    const duplicatePrivateIds = Object.entries(groupedByPrivateId)
      .filter(([, group]) => group.length > 1)
      .map(([privateId]) => privateId);

    if (duplicatePrivateIds.length > 0) {
      notifyError(
        `You can only connect one instance of an app per Partner Dashboard. Choose one store for ${duplicatePrivateIds
          .slice(0, 5)
          .join(', ')} to connect.`,
      );

      return false;
    }

    return onSubmit?.(ids);
  };

  render() {
    const {
      title = 'Connect apps',
      action = (count) =>
        count > 0
          ? `Connect ${count} app${count === 1 ? '' : 's'}`
          : 'Connect apps',
      width = 860,
      onCancel,
    } = this.props;
    const { loading, collection, disabledRecords, filter, search } = this.state;
    // Kept while searching, so the search can be cleared again
    const showFilter =
      filter !== '' ||
      search !== '' ||
      (collection?.count || 0) > LinkApps.FILTER_THRESHOLD;

    return (
      <SelectRecordsModal
        title={title}
        recordsLabel="apps"
        action={action}
        width={width}
        alert={{
          title:
            'Connecting an app transfers its ownership to this partner dashboard.',
          subtitle: 'You can disconnect it at any time from the Apps page.',
        }}
        collection={collection}
        fields={fields}
        search={search}
        toolbar={
          showFilter ? (
            <div className="link-apps-filter">
              {/* .form-field is what carries the app's input styling */}
              <div className="form-field">
                <input
                  id="link-apps-filter"
                  type="text"
                  value={filter}
                  onChange={this.onChangeFilter}
                  placeholder="Search apps"
                  aria-label="Search apps"
                />
              </div>
            </div>
          ) : null
        }
        loading={loading}
        disabledRecords={disabledRecords}
        getDisabledWithSelection={this.getConflictedApps}
        getSelectAllExcept={this.getSelectAllExcept}
        onClickPage={this.onClickPage}
        onSubmit={this.onSubmit}
        onCancel={onCancel}
      />
    );
  }
}

export default connect(null, mapDispatchToProps)(LinkApps);
